import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { QuestionOptionsContext, useQuestionOptions } from './QuestionOptionsContext';
import type { QuestionOptionPayload, QuestionType } from '@/types';
import type { TransformOptions } from '@/components/dialogs/options/TransformOptionsSection';

export const ManualRowOptionsContext = createContext<((value: TransformOptions) => void) | null>(null);
export const useManualRowOptions = () => useContext(ManualRowOptionsContext);

/**
 * Row settings no options dialog writes with `setOption`/`clearOption` — the
 * model applied to every row from 동형문제's "전체 적용". Everything else in a
 * row belongs to the row type's options dialog (rows drop their options when
 * the type changes), including the passage-transformation keys.
 */
const ROW_SHARED_OPTION_KEYS = [
  'manual_model', 'manual_effort', 'manual_model_provider', 'manual_model_resolved', 'manual_model_resolved_effort',
];

/**
 * Isolate option dialogs from the persisted, type-wide preferences.
 * `onSave(payload)` merges into the row. `onSave({}, keepOnly)` is a dialog
 * reset (`clearOption`): the caller drops every row key not in `keepOnly` and
 * keeps the row entry itself, so the row stops falling back to the type-wide options.
 */
export function ManualRowOptionsProvider({ type, initialOptions, onSave, onApplyAll, children }: {
  type: QuestionType;
  initialOptions: QuestionOptionPayload;
  onSave: (payload: QuestionOptionPayload, keepOnly?: readonly string[]) => void;
  onApplyAll: (value: TransformOptions) => void;
  children: React.ReactNode;
}) {
  const parent = useQuestionOptions();
  // Freeze the opening snapshot so applying transform settings does not reset unsaved controls.
  const [snapshot] = useState(initialOptions);
  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  const value = useMemo(() => ({
    ...parent,
    options: { [type]: snapshot },
    getOption: () => snapshot,
    setOption: (_type: unknown, payload: QuestionOptionPayload) => saveRef.current(payload),
    clearOption: () => saveRef.current({}, ROW_SHARED_OPTION_KEYS),
    mergeOption: (_type: unknown, patch: QuestionOptionPayload) => {
      const next = { ...snapshot, ...patch };
      saveRef.current(next);
      return next;
    },
    resetAllOptions: () => saveRef.current({}),
  }), [parent, type, snapshot]);
  return <ManualRowOptionsContext.Provider value={onApplyAll}>
    <QuestionOptionsContext.Provider value={value}>{children}</QuestionOptionsContext.Provider>
  </ManualRowOptionsContext.Provider>;
}
